// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import type { LatLon } from '@/features/hole/holeview/tileMath';

/**
 * Reads a latitude and longitude out of whatever an admin pasted.
 *
 * The point of the field is that a coordinate arrives from somewhere else — a maps app, the
 * club's own website, the readout on this very screen — so it has to accept the shapes those
 * produce rather than one canonical format. Anything it cannot read with confidence is
 * rejected outright: silently centring the map on a misparsed number is worse than saying no,
 * because the map will look perfectly plausible over the wrong country.
 *
 * Accepted:
 *   56.6776, 12.8578     a maps app, and this screen's own readout
 *   56.6776 12.8578      whitespace instead of a comma
 *   56,6776 12,8578      decimal commas, which is what a Swedish phone writes
 *   56,6776, 12,8578     decimal commas *and* a separating comma
 *
 * Rejected: anything that does not resolve to exactly two numbers in range. Notably not
 * supported are degrees/minutes/seconds and hemisphere letters ("56°40'N") — they are a
 * different parsing problem, and guessing at them is how you end up in the wrong hemisphere.
 */
export const parseLatLon = (raw: string): LatLon | null => {
  const text = raw.trim();
  if (!text) return null;

  // Read as "separators are commas, semicolons and spaces" first, then as "the commas are
  // decimal points". The first reading is the common one and is tried first; the second only
  // gets a look in when splitting on whitespace alone already yields exactly two pieces, so
  // an ordinary "56.6776, 12.8578" can never be re-read as something else.
  const readings: string[][] = [text.split(/[,;\s]+/).filter(Boolean)];

  const bySpace = text.split(/[;\s]+/).filter(Boolean);
  if (bySpace.length === 2) {
    readings.push(bySpace.map((part) => part.replace(/,$/, '').replace(',', '.')));
  }

  for (const parts of readings) {
    if (parts.length !== 2) continue;

    const lat = Number(parts[0]);
    const lon = Number(parts[1]);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
    if (Math.abs(lat) > 90 || Math.abs(lon) > 180) continue;

    // 0,0 is in the Atlantic and is what an unset field serialises to, so it is far more
    // likely to be a mistake than somewhere anyone means to look at.
    if (Math.abs(lat) < 1e-9 && Math.abs(lon) < 1e-9) continue;

    return { lat, lon };
  }

  return null;
};

/** The form the readout on the map uses, so what is copied out can be pasted back in. */
export const formatLatLon = (point: LatLon): string =>
  `${point.lat.toFixed(5)}, ${point.lon.toFixed(5)}`;
